import { AnyAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { ApiError, ApiResponse } from '../../../../../base/api';
import { Loading } from '../../../../../base/enums';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { RootState } from '../../../../../base/redux/store';
import { isEmptyObject } from '../../../../../base/utils';
import { SignUpParams, ValidateSignUpStageParams } from '../../api/interfaces';
import { PasswordRecoveryStep, SignUpStep } from '../../enums';
import { prepareRecruiterSignUpDataByStep, prepareRecruiterSignUpData } from '../../utils';
import { signUpActions } from '../slices/signUpSlice';
import { loginActions } from '../slices/loginSlice';
import { passwordRecoveryActions } from '../slices/passwordRecoverySlice';
import { recruiterAppActions } from '../../../../redux/slices/recruiterAppSlice';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import { authApi } from '../../api';

function* submitSignUpChangeConfirmationValue({ payload: { newEmail, recaptchaToken, callback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        yield call(authApi.validateSignUpStep, { email: newEmail, validate: true });
        yield put(signUpActions.setField({ id: 'email', value: newEmail }));
        yield put(recruiterAppActions.sendConfirmationCode(newEmail, recaptchaToken));
        callback();
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(signUpActions.setErrors(e.errors));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitSignUp() {
    yield put(appActions.setLoading(Loading.App));
    const { step, fields } = yield select((state: RootState) => state.recruiterAppSignUp);

    try {
        if (![SignUpStep.Confirmation, SignUpStep.Areas].includes(step)) {
            const dataForBackendValidation: ValidateSignUpStageParams = yield prepareRecruiterSignUpDataByStep(
                fields,
                step,
            );
            yield call(authApi.validateSignUpStep, dataForBackendValidation);
            yield put(signUpActions.setSignUpStep(step + 1));

            return;
        } else {
            const signUpData: SignUpParams = yield prepareRecruiterSignUpData(fields);
            const response: ApiResponse<unknown> = yield call(authApi.signUp, signUpData);
            if (window.dataLayer) {
                window.dataLayer.push({ event: 'recruiter_registration' });
            }
            if (response.redirectTo) {
                window.location.href = response.redirectTo;
            }
        }
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.emailVerificationNeeded && step !== SignUpStep.Confirmation) {
                yield put(signUpActions.setSignUpStep(SignUpStep.Confirmation));
                return;
            }
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(signUpActions.setErrors(e.errors));
                yield put(signUpActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitLogin() {
    yield put(appActions.setLoading(Loading.App));
    const { fields } = yield select((state: RootState) => state.recruiterAppLogin);

    try {
        const response: ApiResponse<unknown> = yield call(authApi.login, {
            email: fields.email,
            password: fields.password,
        });
        if (response.redirectTo) {
            window.location.href = response.redirectTo;
        }
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(loginActions.setErrors(e.errors));
            yield put(loginActions.setShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* recoverPassword() {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.recruiterAppPasswordRecovery);

    try {
        const response: ApiResponse<unknown> = yield call(authApi.passwordReset, {
            email: fields.email,
            emailCode: fields.emailCode,
        });
        if (response?.passwordResetToken) {
            yield put(passwordRecoveryActions.setPasswordResetToken(response.passwordResetToken));
            yield put(passwordRecoveryActions.setStep(PasswordRecoveryStep.NewPassword));
        }
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.emailVerificationNeeded && step !== PasswordRecoveryStep.Confirmation) {
                yield put(passwordRecoveryActions.setStep(PasswordRecoveryStep.Confirmation));
                return;
            }
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(passwordRecoveryActions.setErrors(e.errors));
                yield put(passwordRecoveryActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* setNewPassword() {
    yield put(appActions.setLoading(Loading.App));
    const { fields, passwordResetToken } = yield select((state: RootState) => state.recruiterAppPasswordRecovery);

    try {
        const response: ApiResponse<unknown> = yield call(authApi.passwordSetNew, {
            password: fields.password,
            passwordResetToken: passwordResetToken,
        });
        if (response.redirectTo) {
            window.location.href = response.redirectTo;
        }
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(passwordRecoveryActions.setErrors(e.errors));
            yield put(passwordRecoveryActions.setShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitPasswordRecoveryChangeConfirmationValue({
    payload: { newValue, recaptchaToken, callback },
}: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        yield call(authApi.passwordReset, {
            email: newValue,
        });
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.emailVerificationNeeded) {
                yield put(passwordRecoveryActions.setField({ id: 'email', value: newValue }));
                yield put(recruiterAppActions.sendConfirmationCode(newValue, recaptchaToken));
                callback();
                return;
            }

            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(passwordRecoveryActions.setErrors(e.errors));
                yield put(passwordRecoveryActions.setShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* authSaga() {
    yield takeLatest(signUpActions.submitChangeConfirmationValue, submitSignUpChangeConfirmationValue);
    yield takeLatest(signUpActions.submitSignUp, submitSignUp);

    yield takeLatest(loginActions.submitLogin, submitLogin);

    yield takeLatest(passwordRecoveryActions.submitRecoverPassword, recoverPassword);
    yield takeLatest(passwordRecoveryActions.submitSetNewPassword, setNewPassword);
    yield takeLatest(
        passwordRecoveryActions.submitChangeConfirmationValue,
        submitPasswordRecoveryChangeConfirmationValue,
    );
}
